'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * shadcn-style Button — compatible con Next.js 15 + Tailwind 4.
 *
 * Variants:
 * - default: primary brand (blue) con sombra y hover-lift
 * - secondary: glass blanco translúcido para CTAs secundarios
 * - outline: borde fino sobre fondo claro
 * - ghost: sin bordes, sólo hover
 * - dark: fondo slate-900 (para hero overlays / cierres)
 * - destructive: rojo (para acciones críticas)
 *
 * Sizes: sm | default | lg | xl | icon
 */
const buttonVariants = cva(
    [
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold tracking-tight',
        'transition-all duration-200 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        'disabled:pointer-events-none disabled:opacity-50',
        'active:scale-[0.97]',
        '[&_svg]:size-4 [&_svg]:shrink-0',
    ].join(' '),
    {
        variants: {
            variant: {
                default:
                    'bg-blue-600 text-white shadow-[0_4px_16px_rgba(37,99,235,0.3)] hover:bg-blue-700 hover:shadow-[0_8px_24px_rgba(37,99,235,0.4)] hover:-translate-y-0.5',
                secondary:
                    'border border-slate-200/70 bg-white/65 text-slate-800 backdrop-blur-sm hover:bg-white/90 hover:border-slate-300 hover:-translate-y-0.5',
                outline:
                    'border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-50 hover:border-slate-400',
                ghost: 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900',
                dark: 'bg-slate-900 text-white shadow-[0_4px_16px_rgba(15,23,42,0.4)] hover:bg-slate-800',
                destructive: 'bg-red-600 text-white shadow-[0_4px_12px_rgba(220,38,38,0.3)] hover:bg-red-700',
            },
            size: {
                sm: 'h-9 px-4 text-[13px]',
                default: 'h-11 px-5 text-sm',
                lg: 'h-13 px-7 text-[15px] [&_svg]:size-[18px]',
                xl: 'h-14 px-8 text-base [&_svg]:size-5',
                icon: 'h-10 w-10 [&_svg]:size-[18px]',
            },
        },
        defaultVariants: { variant: 'default', size: 'default' },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
